import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { postApi } from '../services/apiServices';
import { toast } from 'react-toastify';

// Shared props for the small line icons on this page.
const lineIcon = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
};

const ArrowIcon = () => (
    <svg {...lineIcon} strokeWidth={2.2} className="r4l-ct-btn__arrow" aria-hidden="true">
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
    </svg>
);

const GlobeIcon = () => (
    <svg {...lineIcon}>
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);

const AVATARS = [
    '/images/harry_young_casual_woman_cosmopolitan_ethnic_international_travelling_p_10e28dcb-31c1-4cef-95f2-9aa004abb69a.png',
    '/images/harry_young_business_man_cosmopolitan_ethnic_international_travelling_p_3251416e-657b-4499-a483-589e19e694a4.png',
    '/images/harry_casual_woman_cosmopolitan_ethnic_international_travelling_profile_6d46a6c7-b595-4237-8429-4e491c62d5aa.png',
];

// English defaults; translations live under contact.page.features.<key>.
const features = [
    {
        key: 'quick',
        title: 'Quick responses',
        desc: 'We usually reply within a few hours',
        icon: <svg {...lineIcon}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /></svg>,
    },
    {
        key: 'experts',
        title: 'Travel experts',
        desc: 'Real people who understand travel',
        icon: (
            <svg {...lineIcon}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
    },
    {
        key: 'english',
        title: 'Support in English',
        desc: 'Clear and simple assistance',
        icon: <GlobeIcon />,
    },
    {
        key: 'everyStep',
        title: 'Here at every step',
        desc: 'Before, during and after your trip',
        icon: (
            <svg {...lineIcon}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <path d="M12 13.5s-3-1.8-3-3.6A1.6 1.6 0 0 1 12 9a1.6 1.6 0 0 1 3 .9c0 1.8-3 3.6-3 3.6z" />
            </svg>
        ),
    },
];

// FAQ has no category pages yet, so each card links to the closest existing page.
const quickLinks = [
    {
        key: 'installation',
        to: '/activateEsim',
        title: 'eSIM installation',
        desc: 'Step by step guides',
        blue: true,
        icon: (
            <svg {...lineIcon}>
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
        ),
    },
    {
        key: 'orders',
        to: '/Faq',
        title: 'Orders & payments',
        desc: 'Manage your purchase',
        icon: (
            <svg {...lineIcon}>
                <rect x="4" y="3" width="16" height="18" rx="2" />
                <path d="M8 9h8M8 13h8M8 17h5" />
            </svg>
        ),
    },
    {
        key: 'coverage',
        to: '/destination',
        title: 'Coverage & destinations',
        desc: 'Check country availability',
        icon: <GlobeIcon />,
    },
    {
        key: 'data',
        to: '/speedPolicy',
        title: 'Data usage',
        desc: 'Speed, limits and policies',
        icon: (
            <svg {...lineIcon} strokeWidth={2.2}>
                <path d="M5 20v-4M10 20v-8M15 20v-12M20 20V4" />
            </svg>
        ),
    },
    {
        key: 'refunds',
        to: '/refund-policy',
        title: 'Refunds',
        desc: 'Eligibility and process',
        icon: (
            <svg {...lineIcon}>
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="4" />
                <path d="m4.93 4.93 4.24 4.24M14.83 14.83l4.24 4.24M14.83 9.17l4.24-4.24M4.93 19.07l4.24-4.24" />
            </svg>
        ),
    },
];

const ContactUs = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile_number: '',
        topic: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(true);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateMobile = (mobile) => {
        const mobileRegex = /^\+?[\d\s\-\(\)]{10,}$/;
        return mobileRegex.test(mobile);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('contact.form.validation.nameRequired');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('contact.form.validation.nameMinLength');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('contact.form.validation.emailRequired');
        } else if (!validateEmail(formData.email)) {
            newErrors.email = t('contact.form.validation.emailInvalid');
        }

        if (!formData.mobile_number.trim()) {
            newErrors.mobile_number = t('contact.form.validation.mobileRequired');
        } else if (!validateMobile(formData.mobile_number)) {
            newErrors.mobile_number = t('contact.form.validation.mobileInvalid');
        }

        if (!formData.topic) {
            newErrors.topic = t('contact.form.validation.topicRequired');
        }

        if (!formData.message.trim()) {
            newErrors.message = t('contact.form.validation.messageRequired');
        } else if (formData.message.trim().length < 10) {
            newErrors.message = t('contact.form.validation.messageMinLength');
        }

        if (!termsAccepted) {
            newErrors.terms = t('contact.form.validation.termsRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        
        if (id === 'mobile_number') {
          
            const hasAlphabets = /[a-zA-Z]/.test(value);
            if (hasAlphabets) {
                return; 
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));

        if (errors[id]) {
            setErrors(prev => ({
                ...prev,
                [id]: ''
            }));
        }
    };

    const handleKeyDown = (e) => {
        const { id, key } = e.target;
        
        if (id === 'mobile_number') {
          
            const isAlphabet = /^[a-zA-Z]$/.test(key);
            if (isAlphabet) {
                e.preventDefault(); 
            }
        }
    };

    const handleTermsChange = (e) => {
        setTermsAccepted(e.target.checked);
        if (errors.terms) {
            setErrors(prev => ({
                ...prev,
                terms: ''
            }));
        }
    };

    const scrollToForm = (e) => {
        e.preventDefault();
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error(t('contact.form.validation.formErrors'));
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                mobile_number: formData.mobile_number.trim(),
                topic: formData.topic,
                message: formData.message.trim()
            };

            const response = await postApi('/api/contact-us', payload, { showSuccessToast: false });
            
            if (response?.status) {
                toast.success(response?.message || t('contact.form.success'));
                
                
                setFormData({
                    name: '',
                    email: '',
                    mobile_number: '',
                    topic: '',
                    message: ''
                });
                setErrors({});
            } else {
                toast.error(response?.message || t('contact.form.error'));
            }
        } catch (error) {
            console.error('Contact form submission error:', error);
            
            let errorMessage = t('contact.form.error');
            
            if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* ── Hero ─────────────────────────────────────────────────── */}
            <section className="r4l-ct-hero">
                <img
                    className="r4l-ct-hero__img"
                    src="/images/r4l/contact-hero.webp"
                    alt={t('contact.page.heroAlt', 'Smiling Roam4Less support agent wearing a headset')}
                />
                <div className="r4l-ct-wrap r4l-ct-hero__inner">
                    <p className="r4l-ct-kicker">{t('r4l.pages.contactKicker')}</p>
                    <h1 className="r4l-ct-title">
                        <span>{t('contact.page.titleLine1', 'Here for you')}</span>
                        <span className="r4l-ct-title__accent">{t('contact.page.titleLine2', 'wherever you are.')}</span>
                    </h1>
                    <p className="r4l-ct-sub">
                        {t('contact.page.sub', 'Our support team is here before, during and after your trip. Fast, friendly and always happy to help.')}
                    </p>

                    <ul className="r4l-ct-features">
                        {features.map((f) => (
                            <li key={f.key}>
                                <span className="r4l-ct-features__icon" aria-hidden="true">{f.icon}</span>
                                <strong>{t(`contact.page.features.${f.key}.title`, f.title)}</strong>
                                <span>{t(`contact.page.features.${f.key}.desc`, f.desc)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── Support channels ─────────────────────────────────────── */}
            <section className="r4l-ct-channels">
                <div className="r4l-ct-wrap r4l-ct-channels__grid">

                    {/* Live Support (opens WhatsApp) */}
                    <article className="r4l-ct-card r4l-ct-card--live">
                        <div className="r4l-ct-card__inner">
                            <span className="r4l-ct-card__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
                                    <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
                                </svg>
                            </span>
                            <div className="r4l-ct-card__body">
                                <h2>{t('contact.cards.live.title')}</h2>
                                <p>{t('contact.cards.live.desc')}</p>
                                <span className="r4l-ct-pill">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                    {t('contact.cards.live.hours', 'Support hours: 9:00 AM – 6:00 PM')}
                                </span>
                                <a
                                    href={`https://wa.me/${t('contact.whatsapp.number').replace(/[^\d]/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="r4l-ct-btn r4l-ct-btn--solid"
                                    id="contact-start-chat-btn"
                                >
                                    {t('contact.cards.live.cta')}
                                    <ArrowIcon />
                                </a>
                            </div>
                            <div className="r4l-ct-card__aside" aria-hidden="true">
                                <div className="r4l-ct-avatars">
                                    {AVATARS.map((src) => (
                                        <img key={src} src={src} alt="" loading="lazy" />
                                    ))}
                                    <span className="r4l-ct-avatars__online" />
                                </div>
                                <p className="r4l-ct-scribble">
                                    {t('contact.page.scribbleLine1', 'Real people.')}<br />
                                    {t('contact.page.scribbleLine2', 'Real support.')}
                                </p>
                            </div>
                        </div>
                    </article>

                    {/* Email */}
                    <article className="r4l-ct-card r4l-ct-card--email">
                        <span className="r4l-ct-card__icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                <path d="m22 7-10 6L2 7" />
                            </svg>
                        </span>
                        <div className="r4l-ct-card__body">
                            <h2>{t('contact.cards.email.title')}</h2>
                            <p>{t('contact.cards.email.desc')}</p>
                            <a href={`mailto:${t('contact.email.address')}`} className="r4l-ct-pill r4l-ct-pill--link">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="2" y="4" width="20" height="16" rx="2" />
                                    <path d="m22 7-10 6L2 7" />
                                </svg>
                                {t('contact.email.address')}
                            </a>
                            <a href="#contact-form" onClick={scrollToForm} className="r4l-ct-btn r4l-ct-btn--outline">
                                {t('contact.page.emailCta', 'Send us a message')}
                                <ArrowIcon />
                            </a>
                        </div>
                        <img
                            className="r4l-ct-card__media"
                            src="/images/r4l/contact-email.webp"
                            alt=""
                            loading="lazy"
                        />
                    </article>

                </div>
            </section>

            {/* ── Quick answers ────────────────────────────────────────── */}
            <section className="r4l-ct-quick">
                <div className="r4l-ct-wrap">
                    <h2 className="r4l-ct-quick__title">{t('contact.page.quick.title', 'Looking for a quick answer?')}</h2>
                    <p className="r4l-ct-quick__sub">
                        <Trans
                            i18nKey="contact.page.quick.sub"
                            defaults="You might find it in our <0>FAQs</0>."
                            components={[<Link to="/Faq" />]}
                        />
                    </p>
                    <div className="r4l-ct-quick__grid">
                        {quickLinks.map((q) => (
                            <Link key={q.key} to={q.to} className="r4l-ct-quick__card">
                                <span className={`r4l-ct-quick__icon${q.blue ? ' is-blue' : ''}`} aria-hidden="true">{q.icon}</span>
                                <span className="r4l-ct-quick__text">
                                    <strong>{t(`contact.page.quick.items.${q.key}.title`, q.title)}</strong>
                                    <span>{t(`contact.page.quick.items.${q.key}.desc`, q.desc)}</span>
                                </span>
                                <svg className="r4l-ct-quick__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="m9 18 6-6-6-6" />
                                </svg>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Contact form ─────────────────────────────────────────── */}
            <section className="contact-form" id="contact-form">
                <div className="container">
                    <div className="contact-form-main">
                        <h3>
                            <span dangerouslySetInnerHTML={{ __html: t('contact.form.title') }} />
                        </h3>
                        <p>
                            <span dangerouslySetInnerHTML={{ __html: t('contact.form.description') }} />
                        </p>
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="name">{t('contact.form.fields.name')} *</label>
                                        <input 
                                            type="text" 
                                            className={`form-control ${errors.name ? 'error' : ''}`}
                                            id="name" 
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.form.placeholders.name')}
                                        />
                                        {errors.name && <span className="error-message">{errors.name}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="email">{t('contact.form.fields.email')} *</label>
                                        <input 
                                            type="email" 
                                            className={`form-control ${errors.email ? 'error' : ''}`}
                                            id="email" 
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.form.placeholders.email')}
                                        />
                                        {errors.email && <span className="error-message">{errors.email}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="mobile_number">{t('contact.form.fields.mobileNumber')} *</label>
                                        <input 
                                            type="text" 
                                            className={`form-control ${errors.mobile_number ? 'error' : ''}`}
                                            id="mobile_number" 
                                            value={formData.mobile_number}
                                            onChange={handleInputChange}
                                            onKeyDown={handleKeyDown}
                                            placeholder={t('contact.form.placeholders.mobileNumber')}
                                        />
                                        {errors.mobile_number && <span className="error-message">{errors.mobile_number}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label htmlFor="topic">{t('contact.form.fields.topic')} *</label>
                                        <select 
                                            className={`form-control ${errors.topic ? 'error' : ''}`}
                                            id="topic"
                                            value={formData.topic}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">{t('contact.form.placeholders.topic')}</option>
                                            <option value="activation-problem">{t('contact.form.topics.activationProblem')}</option>
                                            <option value="login-issue">{t('contact.form.topics.loginIssue')}</option>
                                            <option value="payment-failure">{t('contact.form.topics.paymentFailure')}</option>
                                            <option value="bug-report">{t('contact.form.topics.bugReport')}</option>
                                            <option value="other">{t('contact.form.topics.other')}</option>
                                        </select>
                                        {errors.topic && <span className="error-message">{errors.topic}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label htmlFor="message">{t('contact.form.fields.message')} *</label>
                                        <textarea 
                                            className={`form-control message ${errors.message ? 'error' : ''}`}
                                            id="message" 
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            placeholder={t('contact.form.placeholders.message')}
                                            rows="5"
                                        ></textarea>
                                        {errors.message && <span className="error-message">{errors.message}</span>}
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <label className="term">
                                        <Trans i18nKey="contact.form.terms" components={[
                                            <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" />,
                                            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" />
                                        ]} />
                                        <input 
                                            type="checkbox" 
                                            checked={termsAccepted}
                                            onChange={handleTermsChange}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                    {errors.terms && <span className="error-message">{errors.terms}</span>}
                                </div>
                                <div className="col-sm-12">
                                    <button 
                                        type="submit" 
                                        className="send-message" 
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? t('contact.form.sendingButton') : t('contact.form.sendButton')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ContactUs;
